import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getBrands } from '@/api-clients/get-brands';
import { getLocations } from '@/api-clients/get-locations';
import { getPositions } from '@/api-clients/get-positions';
import { useTableContext } from '@/contexts/table-context';
import { useFilterQuery } from '@/hooks/use-filter-query';
import { queryKeys } from '@/query-keys';

export function useDashboardFilters() {
  const intl = useIntl();

  const {
    filters: { positionIds, locationIds, brandIds },
  } = useTableContext();

  const {
    data: positionsData,
    isFetching: isFetchingPositions,
    query: positionsQuery,
    setQuery: setPositionsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.positions,
    fetchFn: getPositions,
    defaultParams: { isSelectedForSourcing: true },
  });

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    query: locationsQuery,
    setQuery: setLocationsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.locations,
    fetchFn: getLocations,
    defaultParams: { isSelectedForSourcing: true },
  });
  const {
    data: brandsData,
    isFetching: isFetchingBrands,
    query: brandsQuery,
    setQuery: setBrandsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.brands,
    fetchFn: getBrands,
    defaultParams: { isSelectedForSourcing: true },
  });

  const brandsOptions = brandsData?.brands?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const filters = useMemo(
    () => [
      {
        filterKey: 'brandIds',
        label: intl.formatMessage({
          defaultMessage: 'Brand',
          description:
            'Dashboard openings filter: filter by employer brand name',
        }),
        isFetching: isFetchingBrands,
        query: brandsQuery,
        setQuery: setBrandsQuery,
        options: brandsOptions,
        isMultiSelect: true,
        selectedValues: brandIds,
      },
      {
        filterKey: 'positionIds',
        label: intl.formatMessage({
          defaultMessage: 'Position',
          description: 'Dashboard openings filter: job title / position',
        }),
        isFetching: isFetchingPositions,
        query: positionsQuery,
        setQuery: setPositionsQuery,
        options: positionsData?.positions?.map(({ id, title }) => ({
          label: title,
          value: id,
        })),
        selectedValues: positionIds,
      },
      {
        filterKey: 'locationIds',
        label: intl.formatMessage({
          defaultMessage: 'Location',
          description: 'Dashboard openings filter: work site or location',
        }),
        isFetching: isFetchingLocations,
        query: locationsQuery,
        setQuery: setLocationsQuery,
        options: locationsData?.locations?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        selectedValues: locationIds,
      },
    ],
    [
      intl,
      isFetchingBrands,
      brandsQuery,
      setBrandsQuery,
      brandsOptions,
      brandIds,
      isFetchingPositions,
      positionsQuery,
      setPositionsQuery,
      positionsData?.positions,
      positionIds,
      isFetchingLocations,
      locationsQuery,
      setLocationsQuery,
      locationsData?.locations,
      locationIds,
    ],
  );

  return filters;
}
